import { Component } from '@angular/core';
import { environment } from './../environments/environment';
import { Router, NavigationStart } from  "@angular/router";
import { Account } from "./models/account";
import { map, filter } from 'rxjs/operators'; // Import operators
import { Platform } from '@ionic/angular';
import { ActionSheetController } from '@ionic/angular';

import { AuthService } from './services/auth.service';
import { ApiService } from './services/api.service';

// Intercom
import { Intercom } from '@capacitor-community/intercom';
import { Intercom as IntercomWeb } from '@intercom/messenger-js-sdk';
import { PushNotifications } from '@capacitor/push-notifications';

import { LocalNotifications } from '@capacitor/local-notifications';
import { LocalNotificationService } from './services/local-notification.service';

// Global PDF
import * as pdfjsLib from 'pdfjs-dist/build/pdf'; // Import PDF.js library

// Capacitor
import { Capacitor } from '@capacitor/core';

// Swiper
import { register } from 'swiper/element/bundle';
import { Local } from 'protractor/built/driverProviders';
import { env } from 'process';
register();

// PDF Worker
//(window as any).pdfWorkerSrc = '/node_modules/pdf.worker.js';

// Component
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  //title = environment.title;
  apiurl = environment.API_URL;
  accounts: Account[];
  selectedAccountId: number;
  selectedAccount: Account;
  currentUser: any;
  isLoggedIn: boolean = false;
  wasLoggedIn = false; // Track the previous logged-in state

  public actionSheetButtons = [
    {
      text: 'Cancel',
      role: 'cancel',
      data: {
        action: 'cancel',
      },
    },
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private actionSheetController: ActionSheetController,
    private apiService: ApiService,
    public platform: Platform,
    private localNotificationService: LocalNotificationService) { 

      this.subscribeToLoginState();

      // Route-level guard handles setup redirects now; keep runtime guard disabled to avoid outlet errors

  }

  private subscribeToLoginState(): void {
    this.authService.loggedIn$.subscribe(async isLoggedIn => {
      console.log('AppComponent: Logged in notification', isLoggedIn);
      this.isLoggedIn = isLoggedIn;

      // Only proceed if transitioning from logged out to logged in
      if (isLoggedIn && !this.wasLoggedIn) {
        console.log('AppComponent: LoggedIn Subscription: Fetching user details');
        try {
          await this.performLoggedInActions();
        } catch (error) {
          console.error('Failed to fetch user details or initialize Intercom:', error);
        }
      }

      this.wasLoggedIn = isLoggedIn; // Update the previous state
    });

    // Bind to account updates
    this.authService.selectedAccount$.subscribe(account => {
      console.log(account);
      // Reload Account data if account is not null
      if (account) {
        this.fetchAccountData();
      }
    });
  }

  private async performLoggedInActions(): Promise<void> {
    // Combine your logic for what should happen upon login
    await this.fetchUserDetails();
    // If user has not completed setup (no name), route to user-setup and stop here
    if (!this.currentUser || !this.currentUser.name) {
      this.router.navigateByUrl('/user-setup');
      return;
    }
    await this.fetchAccountData();

    // Now handled within AuthService
    this.authService.initializePlatformFeatures(this.currentUser);

  }

  async ngOnInit() {
    // Initialize the Intercom Web SDK if not on native platform
    // if (!Capacitor.isNativePlatform()) {
    //   IntercomWeb({
    //     app_id: environment.INTERCOM_IOS_APP_ID,
    //     custom_launcher_selector: '#custom-messenger', // Optional custom launcher selector
    //     hide_default_launcher: true,
    //   });
    // }

    // Set the worker globally
    //pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/workers/pdf.worker.min.js';
    //(window as any).pdfjsWorker = '/assets/workers/pdf.worker.min.js'; // Set the worker globally for ng2-pdf-viewer
    (window as any).pdfWorkerSrc = '/assets/workers/pdf.worker.min.js';
  }

  openSupport() {
    // Intercom
    // If the platform is capacitor, use the Intercom plugin
    if (Capacitor.isNativePlatform()) {
      Intercom.displayMessenger();
    } else {
      //IntercomWeb('show');
    }
  }

  async fetchUserDetails(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService.getUserDetails().subscribe(
        userDetails => {
          this.currentUser = userDetails;
          console.log(this.currentUser);
          resolve(); // Resolve the promise when data is successfully fetched
        },
        error => {
          console.error('Error fetching user details:', error.message);
          reject(error); // Reject the promise on error
        }
      );
    });
  }

  async fetchAccountData(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      // Get selected Account
      this.selectedAccount = await this.authService.getSelectedAccount();
  
      this.authService.getCurrentAccounts().subscribe(
        accounts => {
          accounts = accounts || [];
          if (accounts.length < 1) {
            // If user-setup is pending, do not redirect to account wizard here
            if (this.router.url !== '/user-setup') {
              this.router.navigateByUrl('/account-wizard');
            }
            resolve(); // Resolve here after redirecting to account wizard
          } else {
            this.accounts = accounts;
            resolve(); // Resolve here after accounts have been processed
          }
        },
        error => {
          console.error('Error fetching account data:', error);
          this.logout(); // Handle logout or error as needed
          reject(error); // Reject the promise on error
        }
      );
    });
  }

  private async enforceSetupOnNavigation(urlWithQuery: string): Promise<void> {
    try {
      if (!this.isLoggedIn) { return; }
      const url: string = (urlWithQuery || '').split('?')[0];
      const allow = ['/user-setup', '/account-wizard', '/intro', '/account', '/forgotten-password', '/paywall'];

      console.log('[Guard] Navigating to', url, 'isLoggedIn=', this.isLoggedIn);

      // If user name missing, force user-setup
      const hasName = !!(this.currentUser && typeof this.currentUser.name === 'string' && this.currentUser.name.trim().length > 0);
      console.log('[Guard] hasName=', hasName);
      if (!hasName) {
        if (allow.indexOf(url) === -1) {
          console.log('[Guard] Redirect -> /user-setup');
          this.router.navigateByUrl('/user-setup');
        }
        return;
      }

      // If no selected account, or no accounts at all, force account-wizard
      const selectedAccountId = await this.authService.getSelectedAccountId();
      console.log('[Guard] selectedAccountId=', selectedAccountId);
      if (!selectedAccountId) {
        console.log('[Guard] No selected account, redirecting to account wizard');
        if (allow.indexOf(url) === -1) {
          this.router.navigateByUrl('/account-wizard');
        }
        return;
      }
    } catch (err) {
      console.warn('Navigation guard error', err);
    }
  }
  
  changeAccountButton() {
    this.authService.getCurrentAccounts().pipe(
      map(accounts => {
        return accounts.map(account => ({
          text: account.name,
          handler: () => {
            this.changeAccount(account.id);
          },
        }));
      })
    ).subscribe(
      accountActionSheetButtons => {
        const actionSheet = this.actionSheetController.create({
          buttons: [...accountActionSheetButtons, ...this.actionSheetButtons],
        });
        
        actionSheet.then(sheet => {
          sheet.present();
        });
      },
      error => {
        console.error('Error fetching accounts:', error);
      }
    );
  }

  changeAccount(accountId: any) {
    // Set the selected account and navigate to home screen
    this.authService.setSelectedAccount(accountId).then(
      () => {
        this.fetchAccountData();
        this.router.navigateByUrl('/t/home');
      }
    );
  }

  async logout() {
    this.authService.logout();

    // Go Home
    this.router.navigateByUrl('/intro');
  }

}
